import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, getApiUrl } from '../../../config/api';
import { useMessages } from '../../Messages';

const CuttingTaskInterface = ({ task, readOnly = false }) => {
  const { t } = useTranslation('operator');
  const { showError } = useMessages();

  const config = task?.config || task?.die_cutting_config || {};

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const taskId = task?.task_id;

  const loadSessions = useCallback(async () => {
    if (!taskId) return;
    try {
      setLoadingSessions(true);
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/cutting-sessions`));
      if (res.ok) {
        const data = await res.json();
        setSessions(data || []);
      }
    } catch (err) {
      console.error('Error loading cutting sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Totals
  const totalHours = sessions.reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0).toFixed(2);
  const totalMillares = sessions.reduce((s, r) => s + parseFloat(r.production_millares || 0), 0).toFixed(3);
  const totalWaste = sessions.reduce((s, r) => s + parseFloat(r.waste_kg || 0), 0).toFixed(3);

  return (
    <div className="cutting-task-interface">

      {/* ── Section 1: Especificaciones de Corte ── */}
      <div className="cutting-section spec-section">
        <h4>{t('cutting.sections.specs')}</h4>
        <div className="spec-grid spec-row-1">
          <div className="spec-field">
            <span className="spec-label">{t('labels.productionOrder')}</span>
            <span className="spec-value">{task?.task_code || '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('labels.customer')}</span>
            <span className="spec-value">{task?.customer_name || '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('labels.product')}</span>
            <span className="spec-value">{task?.product_name || task?.task_name || '—'}</span>
          </div>
        </div>
        <div className="spec-grid spec-row-2">
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.materialWidth')}</span>
            <span className="spec-value">{config.material_width_mm ?? '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.cutHeight')}</span>
            <span className="spec-value">{config.cut_height_mm ?? '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.tab')}</span>
            <span className="spec-value">{config.tab_mm ?? '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.edge')}</span>
            <span className="spec-value">{config.edge_mm ?? '—'}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.preCut')}</span>
            <span className="spec-value">{config.has_pre_cut ? t('cutting.values.yes') : t('cutting.values.no')}</span>
          </div>
          <div className="spec-field">
            <span className="spec-label">{t('cutting.fields.preCutType')}</span>
            <span className="spec-value">{config.pre_cut_type || '—'}</span>
          </div>
        </div>
      </div>

      {/* ── Section 2: Registro de Corte (read-only) ── */}
      <div className="cutting-section">
        <div className="section-header">
          <h4>{t('cutting.sections.sessions')}</h4>
        </div>

        {/* Sessions table */}
        {loadingSessions ? (
          <div className="empty-state">{t('cutting.messages.loading')}</div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            {t('cutting.messages.noSessions')}
          </div>
        ) : (
          <div className="sessions-table-wrap">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('cutting.fields.operator')}</th>
                  <th>{t('cutting.fields.date')}</th>
                  <th>{t('cutting.fields.startTime')}</th>
                  <th>{t('cutting.fields.endTime')}</th>
                  <th>{t('cutting.fields.shiftType')}</th>
                  <th>{t('cutting.fields.hoursWorked')}</th>
                  <th>{t('cutting.fields.production')}</th>
                  <th>{t('cutting.fields.waste')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(session => (
                  <tr key={session.session_id}>
                    <td>#{session.session_sequence}</td>
                    <td>{session.operator_name || session.operator_user_id}</td>
                    <td>{session.session_date}</td>
                    <td>{session.start_time}</td>
                    <td>{session.end_time}</td>
                    <td>
                      <span className={`shift-badge shift-${session.shift_type.toLowerCase()}`}>
                        {t(`cutting.shiftTypes.${session.shift_type}`, { defaultValue: session.shift_type })}
                      </span>
                    </td>
                    <td>{parseFloat(session.hours_worked || 0).toFixed(2)}</td>
                    <td className="millares-cell">
                      {parseFloat(session.production_millares || 0).toFixed(3)}
                    </td>
                    <td className={parseFloat(session.waste_kg) > 0 ? 'waste-cell' : ''}>
                      {parseFloat(session.waste_kg || 0).toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="6"><strong>{t('cutting.table.totals')}</strong></td>
                  <td><strong>{totalHours} h</strong></td>
                  <td><strong>{totalMillares}</strong></td>
                  <td><strong>{totalWaste}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <style jsx>{`
        .cutting-task-interface {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .cutting-section {
          margin-bottom: 24px;
          background: white;
          padding: 18px;
          border-radius: 6px;
          border-left: 4px solid #fd7e14;
        }

        .cutting-section h4 {
          margin: 0 0 14px 0;
          color: #2c3e50;
          font-size: 1.05em;
        }

        .spec-section {
          border-left-color: #6c757d;
        }

        .spec-grid {
          display: grid;
          gap: 12px;
          margin-bottom: 10px;
        }

        .spec-row-1 {
          grid-template-columns: repeat(3, 1fr);
        }

        .spec-row-2 {
          grid-template-columns: repeat(6, 1fr);
        }

        @media (max-width: 768px) {
          .spec-row-1 { grid-template-columns: 1fr; }
          .spec-row-2 { grid-template-columns: repeat(2, 1fr); }
        }

        .spec-field {
          display: flex;
          flex-direction: column;
          background: #f8f9fa;
          padding: 10px 12px;
          border-radius: 4px;
        }

        .spec-label {
          font-size: 11px;
          color: #6c757d;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin-bottom: 4px;
        }

        .spec-value {
          font-size: 14px;
          font-weight: 500;
          color: #2c3e50;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .section-header h4 {
          margin: 0;
        }

        /* Table */
        .sessions-table-wrap {
          overflow-x: auto;
        }

        .sessions-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .sessions-table th {
          background: #f8f9fa;
          padding: 10px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
          white-space: nowrap;
        }

        .sessions-table td {
          padding: 10px;
          border-bottom: 1px solid #dee2e6;
          vertical-align: middle;
        }

        .sessions-table tfoot td {
          border-top: 2px solid #dee2e6;
          border-bottom: none;
          background: #f8f9fa;
        }

        .totals-row td {
          padding: 10px;
        }

        .millares-cell {
          font-weight: 600;
          color: #28a745;
        }

        .waste-cell {
          font-weight: 600;
          color: #dc3545;
        }

        /* Shift badges */
        .shift-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
        }

        .shift-badge.shift-hd  { background: #d4edda; color: #155724; }
        .shift-badge.shift-hed { background: #cce5ff; color: #004085; }
        .shift-badge.shift-hn  { background: #e2d9f3; color: #432874; }
        .shift-badge.shift-he  { background: #fde8d8; color: #7d3800; }

        .empty-state {
          text-align: center;
          padding: 30px;
          color: #6c757d;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default CuttingTaskInterface;
